import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Edit2, Trash2, Save, X, ChevronDown, ChevronRight, DollarSign, Users, Target, TrendingUp, Download, Star, Calendar, BarChart3, Table2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import * as XLSX from 'xlsx';
import * as api from '../services/api';
import { toast } from 'sonner';

const fmt = (val) => {
  if (!val && val !== 0) return '-';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(val);
};

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'embudo', label: 'Embudo de Leads' },
  { id: 'calificacion', label: 'Evolucion Semanal Calificacion' },
  { id: 'cpl', label: 'Evolucion CPL' },
];

// Generate all ISO weeks for a given year
const generateISOWeeks = (year) => {
  const weeks = [];
  const jan4 = new Date(year, 0, 4);
  const dow = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dow + 1);
  let cur = new Date(monday);
  let n = 1;
  while (n <= 53) {
    const ws = new Date(cur);
    const we = new Date(cur);
    we.setDate(we.getDate() + 6);
    const pad = (v) => String(v).padStart(2, '0');
    const f = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    weeks.push({ weekNumber: n, weekStart: f(ws), weekEnd: f(we) });
    cur.setDate(cur.getDate() + 7);
    n++;
    const thu = new Date(cur);
    thu.setDate(cur.getDate() + 3);
    if (thu.getFullYear() > year) break;
  }
  return weeks;
};

// Get current ISO week number
const getCurrentISOWeek = () => {
  const now = new Date();
  const jan4 = new Date(now.getFullYear(), 0, 4);
  const dow = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dow + 1);
  const diff = Math.floor((now - monday) / (7 * 24 * 60 * 60 * 1000));
  return { year: now.getFullYear(), week: diff + 1 };
};

const ComportamientosPage = () => {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin';
  const [savedWeeks, setSavedWeeks] = useState([]);
  const [weekStats, setWeekStats] = useState({});
  const [misCampanas, setMisCampanas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingWeekNum, setEditingWeekNum] = useState(null);
  const [expandedWeekNum, setExpandedWeekNum] = useState(null);
  const [weekLeads, setWeekLeads] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('general');
  const currentISOWeek = getCurrentISOWeek();

  const allWeeks = useMemo(() => generateISOWeeks(selectedYear), [selectedYear]);

  const savedByWeekNum = useMemo(() => {
    const map = {};
    savedWeeks.forEach(sw => { map[sw.weekNumber] = sw; });
    return map;
  }, [savedWeeks]);

  const mergedWeeks = useMemo(() => {
    return allWeeks.map(gw => {
      const saved = savedByWeekNum[gw.weekNumber];
      if (saved) return { ...gw, id: saved.id, budget: saved.budget || 0, campaigns: saved.campaigns || [], notes: saved.notes, hasSavedData: true };
      return { ...gw, id: null, budget: 0, campaigns: [], hasSavedData: false };
    });
  }, [allWeeks, savedByWeekNum]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [weeksData, campanasData] = await Promise.all([
        api.getTraffickerWeeks(selectedYear),
        api.getMisCampanas()
      ]);
      setSavedWeeks(weeksData || []);
      setMisCampanas(campanasData || []);
      // Build ISO week lookup for correct date ranges
      const isoWeeks = generateISOWeeks(selectedYear);
      const isoByNum = {};
      isoWeeks.forEach(w => { isoByNum[w.weekNumber] = w; });
      const statsMap = {};
      await Promise.all((weeksData || []).map(async (w) => {
        const iso = isoByNum[w.weekNumber];
        const startDate = iso ? iso.weekStart : w.weekStart;
        const endDate = iso ? iso.weekEnd : w.weekEnd;
        try {
          const s = await api.getTraffickerWeekStats(w.id, startDate, endDate);
          statsMap[w.id] = s;
        } catch { statsMap[w.id] = { campaigns: [], totalLeads: 0, avgRating: 0, ratingDistribution: [0,0,0,0,0], ratedCount: 0 }; }
      }));
      setWeekStats(statsMap);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar datos');
    } finally { setLoading(false); }
  }, [selectedYear]);

  useEffect(() => { loadData(); }, [loadData]);

  const getWeekTotals = useCallback((week) => {
    const stats = week.id ? (weekStats[week.id] || {}) : {};
    const camps = week.campaigns || [];
    const totalBudget = camps.reduce((s, c) => s + (c.budget || 0), 0);
    const totalInvestment = camps.reduce((s, c) => s + (c.realInvestment || 0), 0);
    const totalLeads = stats.totalLeads || 0;
    const costPerLead = totalLeads > 0 ? totalInvestment / totalLeads : 0;
    return { totalBudget, totalInvestment, totalLeads, costPerLead, difference: totalBudget - totalInvestment };
  }, [weekStats]);

  const grandTotals = useMemo(() => {
    let budget = 0, investment = 0;
    mergedWeeks.forEach(w => {
      if (w.hasSavedData) {
        const t = getWeekTotals(w);
        budget += t.totalBudget; investment += t.totalInvestment;
      }
    });
    // Total leads from Mis Campañas (all leads associated to campaigns, no date filter)
    // Only count campaigns that are used in at least one week
    const usedCampaignIds = new Set();
    mergedWeeks.forEach(w => { (w.campaigns || []).forEach(c => { if (c.id) usedCampaignIds.add(c.id); }); });
    const leads = misCampanas.filter(c => usedCampaignIds.has(c.id)).reduce((s, c) => s + (c.leadCount || 0), 0);
    return { budget, investment, leads, costPerLead: leads > 0 ? investment / leads : 0, difference: budget - investment };
  }, [mergedWeeks, getWeekTotals, misCampanas]);

  const handleSaveWeek = async (weekNum, data) => {
    const genWeek = allWeeks.find(w => w.weekNumber === weekNum);
    const saved = savedByWeekNum[weekNum];
    try {
      let result;
      if (saved?.id) {
        result = await api.updateTraffickerWeek(saved.id, data);
      } else {
        result = await api.createTraffickerWeek({
          ...data, weekStart: genWeek.weekStart, weekEnd: genWeek.weekEnd,
          weekNumber: weekNum, year: selectedYear,
        });
      }
      setSavedWeeks(prev => {
        const idx = prev.findIndex(w => w.weekNumber === weekNum);
        if (idx >= 0) return prev.map(w => w.weekNumber === weekNum ? result : w);
        return [...prev, result];
      });
      if (result.id) {
        const genWeek2 = allWeeks.find(w => w.weekNumber === weekNum);
        try {
          const s = await api.getTraffickerWeekStats(result.id, genWeek2?.weekStart, genWeek2?.weekEnd);
          setWeekStats(prev => ({ ...prev, [result.id]: s }));
        } catch {}
      }
      setEditingWeekNum(null);
      toast.success('Semana guardada');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDeleteWeek = async (weekId) => {
    if (!window.confirm('Limpiar datos de esta semana?')) return;
    try { await api.deleteTraffickerWeek(weekId); setSavedWeeks(prev => prev.filter(w => w.id !== weekId)); toast.success('Datos eliminados'); }
    catch { toast.error('Error al eliminar'); }
  };

  const toggleExpand = async (week) => {
    if (!week.id) return;
    const key = week.weekNumber;
    if (expandedWeekNum === key) { setExpandedWeekNum(null); return; }
    setExpandedWeekNum(key);
    if (!weekLeads[week.id]) {
      try { const leads = await api.getTraffickerWeekLeads(week.id, week.weekStart, week.weekEnd); setWeekLeads(prev => ({ ...prev, [week.id]: leads })); }
      catch {}
    }
  };

  const exportToExcel = () => {
    const rows = [];
    mergedWeeks.filter(w => w.hasSavedData).forEach(w => {
      const t = getWeekTotals(w);
      const stats = w.id ? (weekStats[w.id] || {}) : {};
      rows.push({ 'Semana': `S${w.weekNumber}`, 'Periodo': `${w.weekStart} - ${w.weekEnd}`,
        'Presupuesto': t.totalBudget, 'Inversion Real': t.totalInvestment, 'Diferencia': t.difference,
        'Leads': t.totalLeads, 'Costo x Lead': Math.round(t.costPerLead) });
      (stats.campaigns || []).forEach(cs => {
        const wc = (w.campaigns || []).find(c => c.id === cs.id) || {};
        rows.push({ 'Semana': `S${w.weekNumber}`, 'Campana': cs.name,
          'Presupuesto': wc.budget || 0, 'Inversion Real': wc.realInvestment || 0,
          'Diferencia': (wc.budget || 0) - (wc.realInvestment || 0),
          'Leads': cs.leadsTotal, 'Costo x Lead': cs.leadsTotal > 0 ? Math.round((wc.realInvestment || 0) / cs.leadsTotal) : 0 });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comportamientos');
    XLSX.writeFile(wb, `comportamientos_${selectedYear}.xlsx`);
  };

  if (loading) return <div className="p-6 flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>;

  return (
    <div className="p-6 max-w-full" data-testid="comportamientos-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Comportamientos</h1>
          <p className="text-sm text-slate-500 mt-1">Seguimiento semanal de presupuesto, inversion y resultados de pauta</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="year-selector">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg" data-testid="export-comportamientos">
            <Download className="w-4 h-4" /> Exportar
          </button>
        </div>
      </div>

      {/* Summary - only from weeks with data */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Presupuesto', value: fmt(grandTotals.budget), icon: DollarSign, color: 'bg-blue-50 text-blue-700' },
          { label: 'Inversion', value: fmt(grandTotals.investment), icon: TrendingUp, color: 'bg-green-50 text-green-700' },
          { label: 'Diferencia', value: fmt(grandTotals.difference), icon: Target, color: grandTotals.difference >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700' },
          { label: 'Leads', value: grandTotals.leads, icon: Users, color: 'bg-purple-50 text-purple-700' },
          { label: 'Costo/Lead', value: fmt(Math.round(grandTotals.costPerLead)), icon: DollarSign, color: 'bg-slate-50 text-slate-700' },
        ].map(c => { const I = c.icon; return (
          <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
            <div className="flex items-center gap-2 mb-1"><div className={`w-6 h-6 rounded-md flex items-center justify-center ${c.color}`}><I className="w-3.5 h-3.5" /></div><span className="text-xs text-slate-500">{c.label}</span></div>
            <p className="text-lg font-bold text-slate-900">{c.value}</p>
          </div>
        ); })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit">
        {TABS.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid={`tab-${tab.id}`}>{tab.label}</button>)}
      </div>

      {activeTab === 'general' ? (
        <GeneralTab weeks={mergedWeeks} weekStats={weekStats} isAdmin={isAdmin}
          editingWeekNum={editingWeekNum} expandedWeekNum={expandedWeekNum}
          weekLeads={weekLeads} getWeekTotals={getWeekTotals} setEditingWeekNum={setEditingWeekNum}
          handleSaveWeek={handleSaveWeek} handleDeleteWeek={handleDeleteWeek}
          toggleExpand={toggleExpand} misCampanas={misCampanas} currentISOWeek={currentISOWeek}
          selectedYear={selectedYear} />
      ) : activeTab === 'embudo' ? (
        <EmbudoTab weeks={mergedWeeks} weekStats={weekStats} weekLeads={weekLeads}
          setWeekLeads={setWeekLeads} />
      ) : activeTab === 'cpl' ? (
        <CPLEvolutionTab weeks={mergedWeeks} weekStats={weekStats} />
      ) : (
        <CalificacionTab weeks={mergedWeeks.filter(w => w.hasSavedData)} weekStats={weekStats}
          weekLeads={weekLeads} setWeekLeads={setWeekLeads} />
      )}
    </div>
  );
};

// ====== GENERAL TAB ======
const GeneralTab = ({ weeks, weekStats, isAdmin, editingWeekNum, expandedWeekNum, weekLeads,
  getWeekTotals, setEditingWeekNum, handleSaveWeek, handleDeleteWeek, toggleExpand, misCampanas, currentISOWeek, selectedYear }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="overflow-auto max-h-[65vh]">
      <table className="w-full text-sm" data-testid="weeks-table">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="px-3 py-3 w-8 bg-slate-50"></th>
            <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase w-16 bg-slate-50">Sem.</th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase bg-slate-50">Periodo</th>
            <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase bg-slate-50">Campanas</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase bg-slate-50">Presupuesto</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase bg-slate-50">Inversion</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase bg-slate-50">Diferencia</th>
            <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase bg-slate-50">Leads</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase bg-slate-50">Costo/Lead</th>
            <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase w-20 bg-slate-50">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {weeks.map(week => {
            const t = getWeekTotals(week);
            const stats = week.id ? (weekStats[week.id] || {}) : {};
            const isExpanded = expandedWeekNum === week.weekNumber;
            const isEditing = editingWeekNum === week.weekNumber;
            const isCurrent = selectedYear === currentISOWeek.year && week.weekNumber === currentISOWeek.week;
            const hasData = week.hasSavedData;

            return (
              <React.Fragment key={week.weekNumber}>
                {isEditing ? (
                  <WeekEditRow week={week} isAdmin={isAdmin} misCampanas={misCampanas}
                    onSave={data => handleSaveWeek(week.weekNumber, data)}
                    onCancel={() => setEditingWeekNum(null)} />
                ) : (
                  <tr className={`transition-colors ${isCurrent ? 'bg-indigo-50/50 border-l-2 border-l-indigo-500' : hasData ? 'hover:bg-slate-50' : 'hover:bg-slate-50/50'}`}
                    data-testid={`week-row-${week.weekNumber}`}>
                    <td className="px-3 py-2.5">
                      {hasData && <button onClick={() => toggleExpand(week)} className="text-slate-400 hover:text-slate-600">
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${isCurrent ? 'bg-indigo-600 text-white' : hasData ? 'bg-slate-100 text-slate-700' : 'text-slate-400'}`}>
                        {week.weekNumber}
                      </span>
                    </td>
                    <td className={`px-3 py-2.5 whitespace-nowrap ${hasData ? 'font-medium text-slate-900' : 'text-slate-400'}`}>
                      {week.weekStart} a {week.weekEnd}
                      {isCurrent && <span className="ml-2 text-[10px] font-semibold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded">HOY</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {hasData && (week.campaigns || []).length > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
                          {(week.campaigns || []).length}
                        </span>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className={`px-3 py-2.5 text-right ${hasData ? 'font-medium text-blue-700' : 'text-slate-300'}`}>{hasData ? fmt(t.totalBudget) : '-'}</td>
                    <td className={`px-3 py-2.5 text-right ${hasData ? 'font-medium text-green-700' : 'text-slate-300'}`}>{hasData ? fmt(t.totalInvestment) : '-'}</td>
                    <td className={`px-3 py-2.5 text-right font-medium ${!hasData ? 'text-slate-300' : t.difference >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{hasData ? fmt(t.difference) : '-'}</td>
                    <td className={`px-3 py-2.5 text-center ${hasData ? 'font-semibold text-purple-700' : 'text-slate-300'}`}>{hasData ? t.totalLeads : '-'}</td>
                    <td className={`px-3 py-2.5 text-right ${hasData ? 'text-slate-600' : 'text-slate-300'}`}>{hasData && t.totalLeads > 0 ? fmt(Math.round(t.costPerLead)) : '-'}</td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => setEditingWeekNum(week.weekNumber)} className="p-1.5 text-slate-400 hover:text-indigo-600 rounded" title="Editar" data-testid={`edit-week-${week.weekNumber}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {isAdmin && hasData && <button onClick={() => handleDeleteWeek(week.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded" title="Eliminar datos">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>}
                      </div>
                    </td>
                  </tr>
                )}
                {isExpanded && !isEditing && hasData && (
                  <tr><td colSpan={10} className="bg-slate-50 px-6 py-4">
                    <ExpandedContent week={week} stats={stats} leads={weekLeads[week.id]} />
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// ====== EXPANDED CONTENT ======
const ExpandedContent = ({ week, stats, leads }) => (
  <div className="space-y-4">
    <div>
      <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Desglose por Campana (leads calculados automaticamente)</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(stats.campaigns || []).map(cs => {
          const wc = (week.campaigns || []).find(c => c.id === cs.id) || {};
          const cInv = wc.realInvestment || 0, cBudget = wc.budget || 0;
          return (
            <div key={cs.id} className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-900">{cs.name}</span>
                <span className="text-xs text-amber-500">{cs.avgRating > 0 ? `${cs.avgRating} ★` : ''}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span className="text-slate-500">Presupuesto:</span><span className="text-right font-medium">{fmt(cBudget)}</span>
                <span className="text-slate-500">Inversion:</span><span className="text-right font-medium">{fmt(cInv)}</span>
                <span className="text-slate-500">Diferencia:</span><span className={`text-right font-medium ${cBudget - cInv >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(cBudget - cInv)}</span>
                <span className="text-slate-500">Leads:</span><span className="text-right font-semibold text-purple-700">{cs.leadsTotal}</span>
                <span className="text-slate-500">Costo/Lead:</span><span className="text-right font-medium">{cs.leadsTotal > 0 ? fmt(Math.round(cInv / cs.leadsTotal)) : '-'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
    <div>
      <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Leads asociados a campanas ({leads?.length || 0})</h4>
      {leads?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-white border-b">
              <th className="px-2 py-2 text-left">Codigo</th><th className="px-2 py-2 text-left">Nombre</th>
              <th className="px-2 py-2 text-left">Email</th><th className="px-2 py-2 text-left">Etapa</th>
              <th className="px-2 py-2 text-left">Campanas</th>
              <th className="px-2 py-2 text-left">Calif.</th><th className="px-2 py-2 text-left">Fecha</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map(l => (
                <tr key={l.id} className="hover:bg-white cursor-pointer" onClick={() => window.open(`/lead/${l.id}`, '_blank')}>
                  <td className="px-2 py-1.5 font-mono text-indigo-500">{l.code}</td>
                  <td className="px-2 py-1.5 font-medium">{l.name}</td>
                  <td className="px-2 py-1.5 text-slate-500">{l.email}</td>
                  <td className="px-2 py-1.5">
                    {l.stageName ? (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium text-white" style={{ backgroundColor: l.stageColor || '#6366f1' }}>{l.stageName}</span>
                    ) : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {(l.campaignTags || []).map(ct => <span key={ct.tag} className="inline-block bg-indigo-50 text-indigo-600 text-[10px] px-1.5 py-0.5 rounded mr-1">{ct.name || ct.tag}</span>)}
                  </td>
                  <td className="px-2 py-1.5">{l.rating ? <span className="text-amber-400">{'★'.repeat(l.rating)}{'☆'.repeat(5 - l.rating)}</span> : <span className="text-slate-300">-</span>}</td>
                  <td className="px-2 py-1.5 text-slate-500">{l.createdAt ? new Date(l.createdAt).toLocaleDateString('es-CO') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-xs text-slate-400">No se encontraron leads asociados a las campanas de esta semana</p>}
    </div>
  </div>
);


// ====== EMBUDO TAB ======
const EmbudoTab = ({ weeks, weekStats, weekLeads, setWeekLeads }) => {
  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStage, setExpandedStage] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState('all');
  const [selectedComercial, setSelectedComercial] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState('all');

  const savedWeeks = useMemo(() => weeks.filter(w => w.hasSavedData && w.id), [weeks]);

  useEffect(() => {
    const loadAllLeads = async () => {
      setLoading(true);
      try {
        const leadsMap = { ...weekLeads };
        await Promise.all(savedWeeks.map(async (w) => {
          if (!leadsMap[w.id]) {
            try {
              const leads = await api.getTraffickerWeekLeads(w.id, w.weekStart, w.weekEnd);
              leadsMap[w.id] = leads;
            } catch {}
          }
        }));
        setWeekLeads(leadsMap);
        // Deduplicate leads by id
        const seen = new Set();
        const unique = [];
        savedWeeks.forEach(w => {
          (leadsMap[w.id] || []).forEach(l => {
            if (!seen.has(l.id)) { seen.add(l.id); unique.push({ ...l, _weekId: w.id }); }
          });
        });
        setAllLeads(unique);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    loadAllLeads();
  }, [weeks]);

  // Extract unique commercials from leads
  const comercialOptions = useMemo(() => {
    const map = {};
    allLeads.forEach(l => {
      if (l.assignedTo) {
        map[l.assignedTo] = l.assignedToName || l.assignedTo;
      }
    });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allLeads]);

  // Extract unique campaigns from leads
  const campaignOptions = useMemo(() => {
    const map = {};
    allLeads.forEach(l => {
      (l.campaignTags || []).forEach(ct => {
        if (ct.tag) map[ct.tag] = ct.name || ct.tag;
      });
    });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allLeads]);

  const filteredLeads = useMemo(() => {
    let result = allLeads;

    // Week filter
    if (selectedWeek !== 'all') {
      const wk = savedWeeks.find(w => String(w.weekNumber) === selectedWeek);
      if (wk) {
        const wkLeads = weekLeads[wk.id] || [];
        const ids = new Set(wkLeads.map(l => l.id));
        result = result.filter(l => ids.has(l.id));
      }
    }

    // Commercial filter
    if (selectedComercial !== 'all') {
      result = result.filter(l => l.assignedTo === selectedComercial);
    }

    // Campaign filter
    if (selectedCampaign !== 'all') {
      result = result.filter(l => (l.campaignTags || []).some(ct => ct.tag === selectedCampaign));
    }

    return result;
  }, [allLeads, selectedWeek, selectedComercial, selectedCampaign, savedWeeks, weekLeads]);

  const stageGroups = useMemo(() => {
    const groups = {};
    filteredLeads.forEach(l => {
      const key = l.stageId || 'sin-etapa';
      if (!groups[key]) groups[key] = { name: l.stageName || 'Sin etapa', color: l.stageColor || '#94a3b8', leads: [] };
      groups[key].leads.push(l);
    });
    return Object.entries(groups).sort((a, b) => b[1].leads.length - a[1].leads.length);
  }, [filteredLeads]);

  const resetFilters = () => {
    setSelectedWeek('all');
    setSelectedComercial('all');
    setSelectedCampaign('all');
    setExpandedStage(null);
  };

  const hasActiveFilters = selectedWeek !== 'all' || selectedComercial !== 'all' || selectedCampaign !== 'all';

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
    </div>
  );

  const total = filteredLeads.length;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <label className="block text-xs text-slate-500 mb-1">Semana</label>
            <select
              value={selectedWeek}
              onChange={(e) => { setSelectedWeek(e.target.value); setExpandedStage(null); }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              data-testid="embudo-week-filter"
            >
              <option value="all">Todas las semanas</option>
              {savedWeeks.map(w => (
                <option key={w.weekNumber} value={String(w.weekNumber)}>
                  S{w.weekNumber} ({w.weekStart} - {w.weekEnd})
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[180px]">
            <label className="block text-xs text-slate-500 mb-1">Comercial Asignado</label>
            <select
              value={selectedComercial}
              onChange={(e) => { setSelectedComercial(e.target.value); setExpandedStage(null); }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              data-testid="embudo-comercial-filter"
            >
              <option value="all">Todos los comerciales</option>
              {comercialOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[180px]">
            <label className="block text-xs text-slate-500 mb-1">Campana</label>
            <select
              value={selectedCampaign}
              onChange={(e) => { setSelectedCampaign(e.target.value); setExpandedStage(null); }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              data-testid="embudo-campaign-filter"
            >
              <option value="all">Todas las campanas</option>
              {campaignOptions.map(([tag, name]) => (
                <option key={tag} value={tag}>{name}</option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="px-3 py-2 text-xs text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              data-testid="embudo-clear-filters"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">
            De los <span className="text-indigo-600 text-lg font-bold">{total}</span> leads capturados:
          </h3>
          <p className="text-xs text-slate-400">Haz clic en una etapa para ver los leads</p>
        </div>

        <div className="space-y-2">
          {stageGroups.map(([key, group]) => {
            const pct = total > 0 ? (group.leads.length / total * 100) : 0;
            const isOpen = expandedStage === key;
            return (
              <div key={key}>
                <button
                  onClick={() => setExpandedStage(isOpen ? null : key)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                  data-testid={`stage-${key}`}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: group.color }}></span>
                  <span className="font-medium text-slate-900 min-w-[120px]">{group.name}</span>
                  <span className="text-lg font-bold" style={{ color: group.color }}>{group.leads.length}</span>
                  <div className="flex-1 mx-3">
                    <div className="w-full bg-slate-100 rounded-full h-2.5">
                      <div className="h-2.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: group.color }}></div>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 w-12 text-right">{pct.toFixed(0)}%</span>
                </button>

                {isOpen && (
                  <div className="ml-10 mt-1 mb-3 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Codigo</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Nombre</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Email</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Comercial</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Campana</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Calif.</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Fecha</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {group.leads.map(l => (
                          <tr key={l.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => window.open(`/lead/${l.id}`, '_blank')}>
                            <td className="px-2 py-1.5 font-mono text-indigo-500">{l.code}</td>
                            <td className="px-2 py-1.5 font-medium text-slate-900">{l.name}</td>
                            <td className="px-2 py-1.5 text-slate-500">{l.email}</td>
                            <td className="px-2 py-1.5 text-slate-600">{l.assignedToName || <span className="text-slate-300">-</span>}</td>
                            <td className="px-2 py-1.5">
                              {(l.campaignTags || []).map(ct => <span key={ct.tag} className="inline-block bg-indigo-50 text-indigo-600 text-[10px] px-1.5 py-0.5 rounded mr-1">{ct.name || ct.tag}</span>)}
                            </td>
                            <td className="px-2 py-1.5">{l.rating ? <span className="text-amber-400">{'★'.repeat(l.rating)}{'☆'.repeat(5-l.rating)}</span> : <span className="text-slate-300">-</span>}</td>
                            <td className="px-2 py-1.5 text-slate-500">{l.createdAt ? new Date(l.createdAt).toLocaleDateString('es-CO') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          {stageGroups.length === 0 && (
            <p className="text-center py-8 text-slate-400 text-sm">No hay leads capturados en las semanas con datos</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ====== CPL EVOLUTION TAB ======
const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const CPLEvolutionTab = ({ weeks, weekStats }) => {
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [viewMode, setViewMode] = useState('chart'); // 'chart' | 'table'

  // Get all saved weeks sorted chronologically
  const savedWeeks = useMemo(() =>
    weeks.filter(w => w.hasSavedData && w.id).sort((a, b) => a.weekStart.localeCompare(b.weekStart)),
  [weeks]);

  // Extract all unique campaigns across all weeks
  const allCampaigns = useMemo(() => {
    const map = {};
    savedWeeks.forEach(w => {
      (w.campaigns || []).forEach(c => {
        if (c.id && !map[c.id]) map[c.id] = { id: c.id, name: c.name || c.tag || c.id, tag: c.tag };
      });
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [savedWeeks]);

  // Build weekly data series (only weeks with actual financial data)
  const chartData = useMemo(() => {
    return savedWeeks
      .filter(w => {
        const camps = w.campaigns || [];
        if (camps.length === 0) return false;
        const hasFinancials = camps.some(c => (c.budget || 0) > 0 || (c.realInvestment || 0) > 0);
        const stats = weekStats[w.id] || {};
        return hasFinancials || (stats.totalLeads || 0) > 0;
      })
      .map(w => {
      const stats = weekStats[w.id] || {};
      const campaignStats = stats.campaigns || [];

      if (selectedCampaign === 'all') {
        // Aggregate all campaigns in this week
        const totalInvestment = (w.campaigns || []).reduce((s, c) => s + (c.realInvestment || 0), 0);
        const totalBudget = (w.campaigns || []).reduce((s, c) => s + (c.budget || 0), 0);
        const totalLeads = stats.totalLeads || 0;
        const cpl = totalLeads > 0 ? Math.round(totalInvestment / totalLeads) : 0;
        return {
          week: `S${w.weekNumber}`,
          weekNumber: w.weekNumber,
          period: `${w.weekStart} - ${w.weekEnd}`,
          presupuesto: totalBudget,
          inversion: totalInvestment,
          leads: totalLeads,
          cpl,
        };
      } else {
        // Single campaign
        const wCamp = (w.campaigns || []).find(c => c.id === selectedCampaign);
        if (!wCamp) return null;
        const sCamp = campaignStats.find(c => c.id === selectedCampaign);
        const inv = wCamp?.realInvestment || 0;
        const budget = wCamp?.budget || 0;
        const leads = sCamp?.leadsTotal || 0;
        const cpl = leads > 0 ? Math.round(inv / leads) : 0;
        return {
          week: `S${w.weekNumber}`,
          weekNumber: w.weekNumber,
          period: `${w.weekStart} - ${w.weekEnd}`,
          presupuesto: budget,
          inversion: inv,
          leads,
          cpl,
        };
      }
    }).filter(Boolean);
  }, [savedWeeks, weekStats, selectedCampaign]);

  // Summary stats
  const summary = useMemo(() => {
    const totInv = chartData.reduce((s, d) => s + d.inversion, 0);
    const totLeads = chartData.reduce((s, d) => s + d.leads, 0);
    const totBudget = chartData.reduce((s, d) => s + d.presupuesto, 0);
    const avgCPL = totLeads > 0 ? Math.round(totInv / totLeads) : 0;
    // Trend: compare last 2 weeks
    const weeksWithLeads = chartData.filter(d => d.leads > 0);
    let trend = null;
    if (weeksWithLeads.length >= 2) {
      const last = weeksWithLeads[weeksWithLeads.length - 1].cpl;
      const prev = weeksWithLeads[weeksWithLeads.length - 2].cpl;
      trend = prev > 0 ? Math.round(((last - prev) / prev) * 100) : null;
    }
    // Best and worst week
    const withCPL = chartData.filter(d => d.cpl > 0);
    const bestWeek = withCPL.length > 0 ? withCPL.reduce((best, d) => d.cpl < best.cpl ? d : best) : null;
    const worstWeek = withCPL.length > 0 ? withCPL.reduce((worst, d) => d.cpl > worst.cpl ? d : worst) : null;
    return { totInv, totLeads, totBudget, avgCPL, trend, bestWeek, worstWeek, totalWeeks: chartData.length };
  }, [chartData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-semibold text-slate-900 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-slate-600">{p.name}:</span>
            <span className="font-semibold">{p.name === 'Leads' ? p.value : fmt(p.value)}</span>
          </p>
        ))}
      </div>
    );
  };

  // Build per-campaign comparison data (when "all" is selected)
  const campaignComparison = useMemo(() => {
    if (selectedCampaign !== 'all') return [];
    return allCampaigns.map(camp => {
      let totalInv = 0, totalLeads = 0;
      savedWeeks.forEach(w => {
        const wc = (w.campaigns || []).find(c => c.id === camp.id);
        const stats = weekStats[w.id] || {};
        const sc = (stats.campaigns || []).find(c => c.id === camp.id);
        totalInv += wc?.realInvestment || 0;
        totalLeads += sc?.leadsTotal || 0;
      });
      return { ...camp, totalInv, totalLeads, cpl: totalLeads > 0 ? Math.round(totalInv / totalLeads) : 0 };
    }).sort((a, b) => a.cpl - b.cpl);
  }, [selectedCampaign, allCampaigns, savedWeeks, weekStats]);

  if (savedWeeks.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
        <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">No hay semanas con datos registrados</p>
        <p className="text-xs text-slate-400 mt-1">Agrega datos en la pestana General para ver la evolucion del CPL</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="cpl-evolution-tab">
      {/* Controls */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Campana</label>
              <select
                value={selectedCampaign}
                onChange={e => setSelectedCampaign(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white min-w-[200px]"
                data-testid="cpl-campaign-selector"
              >
                <option value="all">Todas las campanas</option>
                {allCampaigns.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('chart')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'chart' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="cpl-view-chart"
            >
              <BarChart3 className="w-4 h-4" /> Grafico
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'table' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="cpl-view-table"
            >
              <Table2 className="w-4 h-4" /> Tabla
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><div className="w-6 h-6 rounded-md flex items-center justify-center bg-indigo-50 text-indigo-700"><DollarSign className="w-3.5 h-3.5" /></div><span className="text-xs text-slate-500">CPL Promedio</span></div>
          <p className="text-lg font-bold text-indigo-700">{fmt(summary.avgCPL)}</p>
          {summary.trend !== null && (
            <p className={`text-xs mt-0.5 font-medium ${summary.trend <= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {summary.trend <= 0 ? '↓' : '↑'} {Math.abs(summary.trend)}% vs semana anterior
            </p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><div className="w-6 h-6 rounded-md flex items-center justify-center bg-green-50 text-green-700"><TrendingUp className="w-3.5 h-3.5" /></div><span className="text-xs text-slate-500">Inversion Total</span></div>
          <p className="text-lg font-bold text-green-700">{fmt(summary.totInv)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><div className="w-6 h-6 rounded-md flex items-center justify-center bg-purple-50 text-purple-700"><Users className="w-3.5 h-3.5" /></div><span className="text-xs text-slate-500">Leads Totales</span></div>
          <p className="text-lg font-bold text-purple-700">{summary.totLeads}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><div className="w-6 h-6 rounded-md flex items-center justify-center bg-emerald-50 text-emerald-700"><Target className="w-3.5 h-3.5" /></div><span className="text-xs text-slate-500">Mejor Semana</span></div>
          <p className="text-lg font-bold text-emerald-700">{summary.bestWeek ? summary.bestWeek.week : '-'}</p>
          {summary.bestWeek ? <p className="text-xs text-slate-400">{fmt(summary.bestWeek.cpl)} / lead</p> : <p className="text-[10px] text-slate-400">Sin datos de CPL</p>}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><div className="w-6 h-6 rounded-md flex items-center justify-center bg-red-50 text-red-700"><Target className="w-3.5 h-3.5" /></div><span className="text-xs text-slate-500">Peor Semana</span></div>
          <p className="text-lg font-bold text-red-700">{summary.worstWeek ? summary.worstWeek.week : '-'}</p>
          {summary.worstWeek ? <p className="text-xs text-slate-400">{fmt(summary.worstWeek.cpl)} / lead</p> : <p className="text-[10px] text-slate-400">Sin datos de CPL</p>}
        </div>
      </div>

      {/* Chart or Table */}
      {viewMode === 'chart' ? (
        <div className="space-y-4">
          {/* CPL Line Chart */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Costo por Lead (CPL) - Evolucion Semanal</h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="cpl" name="CPL" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4, fill: '#6366f1' }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Investment & Leads Bars */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Inversion y Leads por Semana</h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="inversion" name="Inversion" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="leads" name="Leads" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per-campaign CPL comparison (only when "all" selected) */}
          {selectedCampaign === 'all' && campaignComparison.length > 1 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">CPL por Campana (acumulado)</h3>
              <div style={{ width: '100%', height: Math.max(200, campaignComparison.length * 45) }}>
                <ResponsiveContainer>
                  <BarChart data={campaignComparison} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => fmt(v)} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={140} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Bar dataKey="cpl" name="CPL" radius={[0, 4, 4, 0]}>
                      {campaignComparison.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Weekly table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cpl-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Semana</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Periodo</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Presupuesto</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Inversion</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Leads</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">CPL</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Tendencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {chartData.map((row, idx) => {
                    const prev = idx > 0 ? chartData[idx - 1] : null;
                    const trendVal = prev && prev.cpl > 0 && row.cpl > 0 ? Math.round(((row.cpl - prev.cpl) / prev.cpl) * 100) : null;
                    return (
                      <tr key={row.weekNumber} className="hover:bg-slate-50" data-testid={`cpl-row-${row.weekNumber}`}>
                        <td className="px-3 py-3 font-semibold text-slate-900">{row.week}</td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap text-xs">{row.period}</td>
                        <td className="px-3 py-3 text-right font-medium text-blue-700">{fmt(row.presupuesto)}</td>
                        <td className="px-3 py-3 text-right font-medium text-green-700">{fmt(row.inversion)}</td>
                        <td className="px-3 py-3 text-center font-semibold text-purple-700">{row.leads}</td>
                        <td className="px-3 py-3 text-right font-bold text-indigo-700">{row.leads > 0 ? fmt(row.cpl) : <span className="text-slate-300">-</span>}</td>
                        <td className="px-3 py-3 text-center">
                          {trendVal !== null ? (
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${trendVal <= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                              {trendVal <= 0 ? '↓' : '↑'} {Math.abs(trendVal)}%
                            </span>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr className="bg-slate-50 font-semibold border-t-2 border-slate-200">
                    <td className="px-3 py-3 text-slate-900" colSpan={2}>Total</td>
                    <td className="px-3 py-3 text-right text-blue-700">{fmt(summary.totBudget)}</td>
                    <td className="px-3 py-3 text-right text-green-700">{fmt(summary.totInv)}</td>
                    <td className="px-3 py-3 text-center text-purple-700">{summary.totLeads}</td>
                    <td className="px-3 py-3 text-right text-indigo-700">{fmt(summary.avgCPL)}</td>
                    <td className="px-3 py-3 text-center">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Campaign comparison table (when "all") */}
          {selectedCampaign === 'all' && campaignComparison.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900">Resumen por Campana (acumulado)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cpl-campaign-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Campana</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Inversion</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Leads</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">CPL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {campaignComparison.map((c, i) => (
                      <tr key={c.id} className="hover:bg-slate-50">
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                            <span className="font-medium text-slate-900">{c.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-green-700">{fmt(c.totalInv)}</td>
                        <td className="px-3 py-3 text-center font-semibold text-purple-700">{c.totalLeads}</td>
                        <td className="px-3 py-3 text-right font-bold text-indigo-700">{c.totalLeads > 0 ? fmt(c.cpl) : <span className="text-slate-300">-</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ====== CALIFICACION TAB ======
const CalificacionTab = ({ weeks, weekStats, weekLeads, setWeekLeads }) => {
  const [selectedComercial, setSelectedComercial] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [allLeads, setAllLeads] = useState([]);
  const [leadsLoaded, setLeadsLoaded] = useState(false);

  const savedWeeks = useMemo(() => weeks.filter(w => w.id), [weeks]);

  // Load leads for all weeks (reuse cached data)
  useEffect(() => {
    const loadLeads = async () => {
      const leadsMap = { ...weekLeads };
      let changed = false;
      await Promise.all(savedWeeks.map(async (w) => {
        if (!leadsMap[w.id]) {
          try {
            const leads = await api.getTraffickerWeekLeads(w.id, w.weekStart, w.weekEnd);
            leadsMap[w.id] = leads;
            changed = true;
          } catch {}
        }
      }));
      if (changed) setWeekLeads(leadsMap);

      const seen = new Set();
      const unique = [];
      savedWeeks.forEach(w => {
        (leadsMap[w.id] || []).forEach(l => {
          if (!seen.has(l.id)) { seen.add(l.id); unique.push({ ...l, _weekId: w.id }); }
        });
      });
      setAllLeads(unique);
      setLeadsLoaded(true);
    };
    loadLeads();
  }, [weeks]);

  // Extract unique commercials
  const comercialOptions = useMemo(() => {
    const map = {};
    allLeads.forEach(l => {
      if (l.assignedTo) map[l.assignedTo] = l.assignedToName || l.assignedTo;
    });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allLeads]);

  // Extract unique campaigns
  const campaignOptions = useMemo(() => {
    const map = {};
    allLeads.forEach(l => {
      (l.campaignTags || []).forEach(ct => {
        if (ct.tag) map[ct.tag] = ct.name || ct.tag;
      });
    });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allLeads]);

  const hasFilters = selectedComercial !== 'all' || selectedCampaign !== 'all';

  // Compute data: if no filters, use original weekStats; if filters active, recompute from leads
  const data = useMemo(() => {
    if (!hasFilters) {
      // Use pre-computed backend stats (fast path)
      return weeks.map(w => {
        const s = w.id ? (weekStats[w.id] || {}) : {};
        return { weekId: w.id, weekNumber: w.weekNumber, weekStart: w.weekStart, weekEnd: w.weekEnd,
          totalLeads: s.totalLeads || 0, ratedCount: s.ratedCount || 0, avgRating: s.avgRating || 0,
          distribution: s.ratingDistribution || [0,0,0,0,0] };
      }).sort((a, b) => a.weekStart.localeCompare(b.weekStart));
    }

    // Filtered path: recompute stats from leads
    return savedWeeks.map(w => {
      let leads = weekLeads[w.id] || [];

      if (selectedComercial !== 'all') {
        leads = leads.filter(l => l.assignedTo === selectedComercial);
      }
      if (selectedCampaign !== 'all') {
        leads = leads.filter(l => (l.campaignTags || []).some(ct => ct.tag === selectedCampaign));
      }

      const totalLeads = leads.length;
      const rated = leads.filter(l => l.rating && l.rating > 0);
      const ratedCount = rated.length;
      const avgRating = ratedCount > 0 ? rated.reduce((s, l) => s + l.rating, 0) / ratedCount : 0;
      const distribution = [0, 0, 0, 0, 0];
      rated.forEach(l => { if (l.rating >= 1 && l.rating <= 5) distribution[l.rating - 1]++; });

      return { weekId: w.id, weekNumber: w.weekNumber, weekStart: w.weekStart, weekEnd: w.weekEnd,
        totalLeads, ratedCount, avgRating, distribution };
    }).sort((a, b) => a.weekStart.localeCompare(b.weekStart));
  }, [weeks, weekStats, savedWeeks, weekLeads, hasFilters, selectedComercial, selectedCampaign]);

  const maxLeads = Math.max(...data.map(s => s.totalLeads), 1);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <label className="block text-xs text-slate-500 mb-1">Comercial Asignado</label>
            <select
              value={selectedComercial}
              onChange={(e) => setSelectedComercial(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              data-testid="calificacion-comercial-filter"
            >
              <option value="all">Todos los comerciales</option>
              {comercialOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[180px]">
            <label className="block text-xs text-slate-500 mb-1">Campana</label>
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              data-testid="calificacion-campaign-filter"
            >
              <option value="all">Todas las campanas</option>
              {campaignOptions.map(([tag, name]) => (
                <option key={tag} value={tag}>{name}</option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <button
              onClick={() => { setSelectedComercial('all'); setSelectedCampaign('all'); }}
              className="px-3 py-2 text-xs text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              data-testid="calificacion-clear-filters"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-400" /> Evolucion de Calificacion por Semana (basada en leads de campanas)
        </h3>
        {data.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No hay datos</p> : (
          <div className="overflow-x-auto">
            <div className="flex gap-4 min-w-[600px] items-end" style={{ minHeight: 200 }}>
              {data.map(ws => {
                const barH = maxLeads > 0 ? (ws.totalLeads / maxLeads) * 160 : 0;
                const clr = ws.avgRating >= 4 ? '#22c55e' : ws.avgRating >= 3 ? '#eab308' : ws.avgRating >= 2 ? '#f97316' : ws.avgRating > 0 ? '#ef4444' : '#cbd5e1';
                return (
                  <div key={ws.weekId || ws.weekNumber} className="flex flex-col items-center flex-1 min-w-[80px]">
                    <div className="text-center mb-2">
                      <div className="text-2xl font-bold" style={{ color: clr }}>{ws.avgRating > 0 ? ws.avgRating.toFixed(1) : '-'}</div>
                      <div className="flex gap-0.5 justify-center">{[1,2,3,4,5].map(i => <span key={i} className={`text-xs ${i <= Math.round(ws.avgRating) ? 'text-amber-400' : 'text-slate-200'}`}>&#9733;</span>)}</div>
                    </div>
                    <div className="w-full flex flex-col items-center">
                      <div className="w-10 rounded-t-md transition-all" style={{ height: Math.max(barH, 4), backgroundColor: clr, opacity: 0.7 }} />
                    </div>
                    <div className="text-center mt-2">
                      <p className="text-xs font-semibold text-slate-700">S{ws.weekNumber}</p>
                      <p className="text-[10px] text-slate-400">{ws.totalLeads} leads</p>
                      <p className="text-[10px] text-slate-400">{ws.ratedCount} calif.</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Semana</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Periodo</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Leads</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Calificados</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Promedio</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">1★</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">2★</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">3★</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">4★</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">5★</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((ws, idx) => {
                const prev = idx > 0 ? data[idx - 1] : null;
                const trend = prev && prev.avgRating > 0 && ws.avgRating > 0 ? ws.avgRating - prev.avgRating : null;
                return (
                  <tr key={ws.weekId || ws.weekNumber} className="hover:bg-slate-50">
                    <td className="px-3 py-3 font-semibold text-slate-900">S{ws.weekNumber}</td>
                    <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{ws.weekStart} a {ws.weekEnd}</td>
                    <td className="px-3 py-3 text-center font-medium">{ws.totalLeads}</td>
                    <td className="px-3 py-3 text-center">{ws.ratedCount}</td>
                    <td className="px-3 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className="font-bold text-amber-500">{ws.avgRating > 0 ? ws.avgRating.toFixed(1) : '-'}</span>
                        {trend !== null && <span className={`text-xs font-medium ${trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-slate-400'}`}>{trend > 0 ? '+' : ''}{trend.toFixed(1)}</span>}
                      </div>
                    </td>
                    {ws.distribution.map((count, i) => (
                      <td key={i} className="px-3 py-3 text-center">
                        {count > 0 ? <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          i === 4 ? 'bg-green-100 text-green-700' : i === 3 ? 'bg-lime-100 text-lime-700' : i === 2 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                        }`}>{count}</span> : <span className="text-slate-300">0</span>}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Formatted number input component
const NumInput = ({ value, onChange, ...props }) => {
  const [raw, setRaw] = useState(String(value || 0));
  const [focused, setFocused] = useState(false);
  const display = focused ? raw : new Intl.NumberFormat('es-CO').format(Number(raw) || 0);
  return (
    <input type="text" inputMode="numeric" value={display}
      onFocus={() => { setFocused(true); setRaw(String(value || 0)); }}
      onBlur={() => { setFocused(false); onChange(Number(raw) || 0); }}
      onChange={e => { const v = e.target.value.replace(/[^\d]/g, ''); setRaw(v); }}
      {...props} />
  );
};

// ====== EDIT ROW (with campaign selector) ======
const WeekEditRow = ({ week, isAdmin, misCampanas, onSave, onCancel }) => {
  const [campaignData, setCampaignData] = useState(week.campaigns || []);

  const toggleCampaign = (camp) => {
    setCampaignData(prev => {
      const exists = prev.find(c => c.id === camp.id);
      if (exists) return prev.filter(c => c.id !== camp.id);
      return [...prev, { id: camp.id, tag: camp.tag, name: camp.name, channel: camp.channel, platform: camp.channel, budget: 0, realInvestment: 0 }];
    });
  };

  const updateCampaign = (idx, field, value) => {
    setCampaignData(prev => { const c = [...prev]; c[idx] = { ...c[idx], [field]: Number(value) || 0 }; return c; });
  };

  // Budget = sum of campaign budgets (auto-calculated, not editable)
  const budget = campaignData.reduce((s, c) => s + (c.budget || 0), 0);
  const totalInv = campaignData.reduce((s, c) => s + (c.realInvestment || 0), 0);

  const handleSave = () => {
    onSave({ campaigns: campaignData, budget });
  };

  return (
    <tr className="bg-indigo-50">
      <td colSpan={10} className="px-4 py-4">
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-500" />
              <span className="text-sm font-semibold text-slate-700">Semana {week.weekNumber}: {week.weekStart} a {week.weekEnd}</span>
            </div>
            <div className="text-sm text-slate-600">Presupuesto semanal: <strong className="text-blue-700">{fmt(budget)}</strong> <span className="text-xs text-slate-400">(suma de presupuestos)</span></div>
          </div>

          {/* Campaign selector */}
          <div>
            <label className="block text-xs text-slate-500 mb-2">Campanas (selecciona las que aplican esta semana)</label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {misCampanas.map(camp => {
                const sel = campaignData.some(c => c.id === camp.id);
                return (
                  <button key={camp.id} type="button" onClick={() => toggleCampaign(camp)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${sel ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${sel ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'}`}>
                      {sel && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <div className="min-w-0"><p className="font-medium truncate">{camp.name}</p><p className="text-xs text-slate-400">{camp.channel}</p></div>
                  </button>
                );
              })}
            </div>
            {misCampanas.length === 0 && <p className="text-xs text-slate-400">No hay campanas. Crealas en "Mis Campanas".</p>}
          </div>

          {/* Campaign budget/investment inputs */}
          {campaignData.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {campaignData.map((c, idx) => (
                <div key={c.id || idx} className="bg-white rounded-lg border p-3">
                  <p className="text-sm font-semibold mb-2">{c.name}</p>
                  <div className="space-y-2">
                    {isAdmin && <div className="flex items-center gap-2"><label className="text-xs text-slate-500 w-24">Presupuesto:</label><NumInput value={c.budget || 0} onChange={v => updateCampaign(idx, 'budget', v)} className="px-2 py-1 text-sm border rounded flex-1" /></div>}
                    <div className="flex items-center gap-2"><label className="text-xs text-slate-500 w-24">Inversion:</label><NumInput value={c.realInvestment || 0} onChange={v => updateCampaign(idx, 'realInvestment', v)} className="px-2 py-1 text-sm border rounded flex-1" /></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 text-sm text-slate-600">
            <span>Total Inversion: <strong>{fmt(totalInv)}</strong></span>
            <span>Diferencia: <strong className={budget - totalInv >= 0 ? 'text-emerald-600' : 'text-red-600'}>{fmt(budget - totalInv)}</strong></span>
            <span className="text-xs text-slate-400">(Los leads se calculan automaticamente)</span>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg" data-testid="save-week-btn"><Save className="w-4 h-4 inline mr-1" /> Guardar</button>
            <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default ComportamientosPage;
